import {
  formatBytes,
  isRestartableStepName,
  type RestartRequestStepName,
  type Run,
  type RunStep,
  type StepName,
} from '@grafx-vdp/shared';

/**
 * Canonical, user-facing pipeline plan.
 *
 * This is the ordered list of steps we show on the run-detail page *before*,
 * *while*, and *after* a run executes — so the operator always sees what the
 * pipeline does, even for steps that have not produced a recorded `RunStep`
 * row yet. Internal bookkeeping steps (load-flow, write-run-*, notify-*,
 * cleanup-dataset) are intentionally omitted; they are not part of the
 * user-facing story.
 */

export type StepDetailFieldKind = 'text' | 'json' | 'bytes';

export interface StepDetailField {
  /**
   * Stable identifier for the field. Also used as the lookup key into the
   * future `RunStep.detail` object (see `resolveStepDetailValue`).
   */
  key: string;
  label: string;
  /** How to render the value. Defaults to `'text'`. */
  kind?: StepDetailFieldKind;
  /**
   * When true the field is hidden entirely if it resolves to no value (instead
   * of rendering a muted "—" placeholder). Used for fields that only exist in
   * some outcomes, e.g. the error report on a failed step.
   */
  optional?: boolean;
}

/** Retired step names mapped to their current equivalents (for older runs). */
const LEGACY_STEP_NAMES: Record<string, StepName> = {
  'download-output-zip': 'download-output-result',
  'unzip-and-rename-pdfs': 'prepare-delivery',
};

/** Normalize a stored step name to the current pipeline vocabulary. */
export function normalizeStepName(name: string): StepName {
  return (LEGACY_STEP_NAMES[name] ?? name) as StepName;
}

export interface StepPlanEntry {
  stepName: StepName;
  /** Friendly, human-readable name. */
  label: string;
  /** One-line explanation of what the step does. */
  description: string;
  /** True when the step runs repeatedly (e.g. polling until ready). */
  repeats?: boolean;
  /** Per-step key/value details surfaced in the expanded panel. */
  detailFields: StepDetailField[];
}

export const STEP_PLAN: readonly StepPlanEntry[] = [
  {
    stepName: 'pickup',
    label: 'Pick up input file',
    description: 'Fetches the source CSV from the configured input (e.g. SFTP pickup folder).',
    detailFields: [
      { key: 'filename', label: 'Filename', optional: true },
      { key: 'size', label: 'Size', kind: 'bytes', optional: true },
      { key: 'outcome', label: 'Outcome', optional: true },
      { key: 'pickupStrategy', label: 'Post-pickup action', optional: true },
      { key: 'pickedUpPath', label: 'Picked-up folder', optional: true },
    ],
  },
  {
    stepName: 'parse-csv',
    label: 'Parse CSV',
    description: 'Parses the file into rows and columns (delimiter auto-detected).',
    detailFields: [
      { key: 'rowCount', label: 'Rows' },
      { key: 'columnCount', label: 'Columns' },
    ],
  },
  {
    stepName: 'persist-rows-to-data-service',
    label: 'Persist rows',
    description:
      'Stores the parsed rows so GraFx Studio can pull them via the data connector.',
    detailFields: [
      { key: 'rowCount', label: 'Rows persisted' },
      { key: 'datasetId', label: 'Dataset id' },
    ],
  },
  {
    stepName: 'create-grafx-output-task',
    label: 'Submit GraFx output task',
    description: 'Hands GraFx the template, output settings, and data connector so Studio can generate output.',
    detailFields: [
      { key: 'requestPayload', label: 'Request payload', kind: 'json' },
      { key: 'taskId', label: 'Returned task id' },
    ],
  },
  {
    stepName: 'poll-output-task',
    label: 'Wait for GraFx output',
    description: 'Polls the GraFx task until generation finishes or fails.',
    repeats: true,
    detailFields: [
      { key: 'checks', label: 'Checks' },
      { key: 'lastStatus', label: 'Last status' },
      { key: 'errorReportUrl', label: 'Error report URL', optional: true },
      { key: 'errorReport', label: 'Error report', kind: 'json', optional: true },
    ],
  },
  {
    stepName: 'download-output-result',
    label: 'Download output result',
    description:
      'Downloads the finished GraFx output (ZIP archive or single PDF, JPG, …) into run storage.',
    detailFields: [
      { key: 'outputKey', label: 'Output file' },
      { key: 'outputSize', label: 'Output size', kind: 'bytes' },
      { key: 'outputKind', label: 'Output type' },
    ],
  },
  {
    stepName: 'prepare-delivery',
    label: 'Prepare for delivery',
    description:
      "Extracts archive output when needed and renames files using the flow's filename convention.",
    detailFields: [{ key: 'fileCount', label: 'Files' }],
  },
  {
    stepName: 'deliver',
    label: 'Deliver output',
    description: 'Uploads the prepared files to the configured output target (e.g. SFTP).',
    detailFields: [
      { key: 'target', label: 'Target' },
      { key: 'fileCount', label: 'Files' },
    ],
  },
];

/** Label for a restartable step (used in retry UI copy). */
export function stepPlanLabel(stepName: StepName): string {
  return STEP_PLAN.find((entry) => entry.stepName === stepName)?.label ?? stepName;
}

/**
 * Authoritative failed pipeline step for restart UI. Prefers the latest failed
 * step in the timeline when it is later than `error_summary.step` — Cloudflare
 * Workflow `step.do` errors often drop our stepName and default to pickup.
 */
export function resolveRunFailedStep(
  run: Run,
  attemptsByStep: Map<StepName, RunStep[]>,
): StepName | null {
  let timelineFailed: StepName | null = null;
  let timelineIndex = -1;

  for (const entry of STEP_PLAN) {
    const attempts = attemptsByStep.get(entry.stepName) ?? [];
    const latest = attempts[attempts.length - 1];
    if (latest?.status === 'failed') {
      const idx = STEP_PLAN.findIndex((e) => e.stepName === entry.stepName);
      if (idx > timelineIndex) {
        timelineIndex = idx;
        timelineFailed = entry.stepName;
      }
    }
  }

  const fromSummary = run.errorSummary ? normalizeStepName(run.errorSummary.step) : null;
  if (!fromSummary) return timelineFailed;

  const summaryIndex = STEP_PLAN.findIndex((e) => e.stepName === fromSummary);
  if (summaryIndex < 0) return timelineFailed ?? fromSummary;
  if (timelineFailed && timelineIndex > summaryIndex) return timelineFailed;
  return fromSummary;
}

/**
 * When a run failed, returns the Workflow checkpoint to restart from for a step
 * row in the timeline (null when restart is not offered for that row).
 */
export function resolveStepRestartOffer(
  run: Run,
  stepName: StepName,
  stepState: 'pending' | 'running' | 'succeeded' | 'failed' | 'retrying',
  attemptsByStep?: Map<StepName, RunStep[]>,
): { fromStep: RestartRequestStepName; usesEarlierCheckpoint: boolean } | null {
  if (run.status !== 'failed') return null;
  if (stepState !== 'succeeded' && stepState !== 'failed') return null;

  const failedStep =
    attemptsByStep != null
      ? resolveRunFailedStep(run, attemptsByStep)
      : run.errorSummary
        ? normalizeStepName(run.errorSummary.step)
        : null;
  const failedIndex = failedStep
    ? STEP_PLAN.findIndex((entry) => entry.stepName === failedStep)
    : -1;
  const stepIndex = STEP_PLAN.findIndex((entry) => entry.stepName === stepName);
  if (failedIndex >= 0 && stepIndex > failedIndex) return null;

  if (stepName === 'poll-output-task') {
    if (stepState !== 'failed') return null;
    return { fromStep: 'poll-output-task', usesEarlierCheckpoint: false };
  }

  if (
    stepState === 'succeeded' &&
    failedIndex >= 0 &&
    stepIndex >= 0 &&
    stepIndex < failedIndex &&
    isRestartableStepName(stepName)
  ) {
    return { fromStep: stepName, usesEarlierCheckpoint: true };
  }

  if (!isRestartableStepName(stepName)) return null;
  return { fromStep: stepName, usesEarlierCheckpoint: false };
}

/** Button label for a step restart offer on the run-detail page. */
export function restartOfferLabel(offer: {
  fromStep: RestartRequestStepName;
  usesEarlierCheckpoint: boolean;
}): string {
  if (offer.fromStep === 'poll-output-task') return 'Retry polling';
  if (offer.usesEarlierCheckpoint) {
    return `Retry from ${stepPlanLabel(offer.fromStep)}`;
  }
  return 'Retry from here';
}

/**
 * Aggregate dataset facts already available to the run-detail page. Used as a
 * fallback source for parse/persist detail fields until the backend records
 * them on the step itself.
 */
export interface StepDatasetFacts {
  total: number;
  columnCount: number;
  datasetId: string;
}

export interface StepDetailContext {
  /** Latest recorded attempt for the step, if any. */
  latest: RunStep | null;
  /** All recorded attempts for the step (sorted by attempt). */
  attempts: RunStep[];
  run: Run;
  dataset: StepDatasetFacts | null;
}

/**
 * Future-proof `RunStep` shape: the backend will (in a later pass) attach a
 * structured `detail` object to each step. We read it opportunistically here
 * so that wiring real values in becomes a one-line change per field.
 */
type RunStepWithDetail = RunStep & { detail?: Record<string, unknown> };

function detailFieldKind(stepName: StepName, fieldKey: string): StepDetailFieldKind | undefined {
  return STEP_PLAN.find((entry) => entry.stepName === stepName)?.detailFields.find(
    (field) => field.key === fieldKey,
  )?.kind;
}

function formatDetailValue(
  value: unknown,
  kind: StepDetailFieldKind | undefined = 'text',
): string | null {
  if (value === null || value === undefined) return null;
  if (kind === 'bytes') {
    const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    if (!Number.isFinite(n)) return null;
    return formatBytes(n);
  }
  if (typeof value === 'string') return value.length === 0 ? null : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
}

/**
 * Resolve the display value for a single step detail field.
 *
 * Resolution order:
 *   1. The future `RunStep.detail[fieldKey]` (the backend will populate this).
 *   2. A small set of fallbacks sourced from data already available today.
 *   3. `null` → rendered as a muted "—" placeholder.
 *
 * To wire in a real backend source later, the structured `detail` object takes
 * precedence automatically; no change needed here for fields the backend fills.
 */
export function resolveStepDetailValue(
  stepName: StepName,
  fieldKey: string,
  ctx: StepDetailContext,
): string | null {
  // 1. Prefer the structured detail object once the backend records it.
  const detail = (ctx.latest as RunStepWithDetail | null)?.detail;
  if (detail && Object.prototype.hasOwnProperty.call(detail, fieldKey)) {
    if (fieldKey === 'pickupStrategy') {
      const value = detail[fieldKey];
      if (value === 'none') return 'No action';
      if (value === 'delete') return 'Delete picked-up file';
      if (value === 'move') return 'Move picked-up file';
    }
    const resolved = formatDetailValue(detail[fieldKey], detailFieldKind(stepName, fieldKey));
    if (resolved !== null) return resolved;
  }

  // 2. Fallbacks from data available today. Everything else returns null.
  switch (`${stepName}:${fieldKey}`) {
    case 'parse-csv:rowCount':
    case 'persist-rows-to-data-service:rowCount':
      return ctx.dataset ? String(ctx.dataset.total) : null;
    case 'parse-csv:columnCount':
      return ctx.dataset && ctx.dataset.columnCount > 0
        ? String(ctx.dataset.columnCount)
        : null;
    case 'persist-rows-to-data-service:datasetId':
      return ctx.dataset?.datasetId ?? (ctx.run.datasetId || null);
    case 'poll-output-task:checks':
      return ctx.attempts.length > 0 ? String(ctx.attempts.length) : null;
    case 'poll-output-task:lastStatus':
      return ctx.latest?.status ?? null;
    case 'deliver:target':
      return ctx.run.outputRefs[0]?.target ?? null;
    case 'deliver:fileCount':
      return ctx.run.outputRefs.length > 0
        ? String(ctx.run.outputRefs.reduce((sum, ref) => sum + ref.fileCount, 0))
        : null;
    default:
      return null;
  }
}
